<script setup lang="ts">
import type { InputProps } from '@/types/ui';
import { Icon } from '@iconify/vue';
/**
 * UiTextInput - Design System Text Input Component
 *
 * Single-line text input for short-form data entry.
 *
 * WHEN TO USE (vs Textarea vs Select):
 * - Short text: names, emails, phone numbers, URLs
 * - Single-line input that doesn't need multiple lines
 * - Search inputs (consider UiSearchInput for dedicated search)
 * - Password entry
 *
 * TYPE GUIDELINES:
 * - text: General text, names, titles
 * - email: Email addresses (triggers email keyboard on mobile)
 * - password: Passwords (masked input)
 * - tel: Phone numbers (triggers number pad on mobile)
 * - url: Website URLs
 * - number: Numeric values only
 * - search: Search queries (use UiSearchInput for full search UX)
 *
 * DON'T USE FOR:
 * - Multi-line content (use UiTextarea)
 * - Predefined choices (use UiSelect)
 * - Search with clear/loading states (use UiSearchInput)
 *
 * @example
 * <UiTextInput v-model="name" label="Full Name" required />
 * <UiTextInput v-model="email" type="email" label="Email" />
 */
import { computed, useId } from 'vue';

export interface UiTextInputProps extends InputProps {
   /** HTML input type */
   type?: 'text' | 'email' | 'password' | 'search' | 'tel' | 'url' | 'number';
   /** Icon displayed on the left side */
   leftIcon?: string;
   /** Icon displayed on the right side */
   rightIcon?: string;
   /** Autocomplete attribute */
   autocomplete?: string;
   /** Maximum length */
   maxlength?: number;
}

const props = withDefaults(defineProps<UiTextInputProps>(), {
   type: 'text',
   size: 'md',
   disabled: false,
   readonly: false,
   required: false,
});

const emit = defineEmits<{
   focus: [event: FocusEvent];
   blur: [event: FocusEvent];
}>();
const modelValue = defineModel<string | number>();
const id = useId();

const hasError = computed(() => !!props.errorMessage);

const wrapperClasses = computed(() => {
   const sizeClasses = {
      sm: 'h-10',
      md: 'h-12',
      lg: 'h-14',
   };

   const baseClasses = [
      'flex items-center gap-2 w-full rounded-full transition-all duration-100',
      'border-0 ring-1 focus-within:ring-2',
      sizeClasses[props.size],
   ];

   // Horizontal padding based on size
   const paddingClasses = {
      sm: 'px-3',
      md: 'px-4',
      lg: 'px-6',
   };
   baseClasses.push(paddingClasses[props.size]);

   if (props.disabled) {
      baseClasses.push('opacity-50 cursor-not-allowed bg-neutral-light');
   }
   else {
      baseClasses.push('bg-white');
   }

   if (hasError.value) {
      baseClasses.push('ring-accent focus-within:ring-accent');
   }
   else {
      baseClasses.push('ring-primary focus-within:ring-primary');
   }

   return baseClasses.join(' ');
});

const inputClasses = computed(() => {
   const sizeClasses = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
   };

   return [
      'flex-1 bg-transparent outline-none placeholder:text-neutral-dark',
      sizeClasses[props.size],
      props.disabled ? 'cursor-not-allowed' : '',
   ].join(' ');
});

const iconClasses = computed(() => {
   const sizeClasses = {
      sm: 'size-4',
      md: 'size-5',
      lg: 'size-6',
   };
   return [sizeClasses[props.size], 'text-neutral-dark flex-shrink-0'].join(' ');
});

const labelClasses = computed(() => {
   return [
      'block text-sm font-medium text-black mb-1.5',
      props.disabled ? 'opacity-50' : '',
   ].join(' ');
});
</script>

<template>
   <div class="flex flex-col">
      <!-- Label -->
      <label v-if="label" :for="id" :class="labelClasses">
         {{ label }}
         <span v-if="required" class="text-accent ml-0.5">*</span>
      </label>

      <!-- Input wrapper -->
      <div :class="wrapperClasses">
         <Icon v-if="leftIcon" :icon="leftIcon" :class="iconClasses" />

         <input
            :id="id"
            v-model="modelValue"
            :type="type"
            :name="name"
            :placeholder="placeholder"
            :disabled="disabled"
            :readonly="readonly"
            :required="required"
            :autocomplete="autocomplete"
            :maxlength="maxlength"
            :class="inputClasses"
            :aria-invalid="hasError"
            :aria-describedby="errorMessage ? `${id}-error` : helperText ? `${id}-helper` : undefined"
            @focus="emit('focus', $event)"
            @blur="emit('blur', $event)"
         />

         <Icon v-if="rightIcon" :icon="rightIcon" :class="iconClasses" />
      </div>

      <!-- Helper text -->
      <span
         v-if="helperText && !errorMessage"
         :id="`${id}-helper`"
         class="text-sm text-neutral-dark mt-1.5"
      >
         {{ helperText }}
      </span>

      <!-- Error message -->
      <span
         v-if="errorMessage"
         :id="`${id}-error`"
         class="text-sm text-accent mt-1.5"
         role="alert"
      >
         {{ errorMessage }}
      </span>
   </div>
</template>
